import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AdminBooking,
  BlockRequest,
  OutlookStatus,
  adminBlockBooking,
  adminConnectOutlook,
  adminDeleteBooking,
  adminDisconnectOutlook,
  adminGetBookings,
  adminGetOutlookStatus,
  adminResyncOutlook,
  adminUpdateBookingStatus,
} from '../../api/adminApi';

const STATUS_BADGE: Record<string, string> = {
  pending: 'bg-yellow-100 text-yellow-700',
  confirmed: 'bg-green-100 text-green-700',
  cancelled: 'bg-gray-100 text-gray-500',
  declined: 'bg-gray-100 text-gray-500',
  expired: 'bg-gray-100 text-gray-400',
  failed: 'bg-red-100 text-red-700',
};

function toDateInputValue(date: Date): string {
  return date.toISOString().slice(0, 10);
}

function formatDateTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString('de-CH', {
      year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

const OutlookConnectionCard: React.FC = () => {
  const [status, setStatus] = useState<OutlookStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setStatus(await adminGetOutlookStatus());
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleConnect = async () => {
    setBusy(true);
    try {
      const { authorizationUrl } = await adminConnectOutlook();
      window.location.href = authorizationUrl;
    } catch (err: any) {
      setError(err.message);
      setBusy(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Outlook-Verbindung wirklich trennen?')) return;
    setBusy(true);
    try {
      await adminDisconnectOutlook();
      await load();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const handleResync = async () => {
    setBusy(true);
    try {
      await adminResyncOutlook();
      await load();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="bg-white rounded-lg shadow p-4 mb-6 text-sm text-gray-400">Lade Outlook-Status…</div>;

  return (
    <div className="bg-white rounded-lg shadow p-4 mb-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-medium text-gray-800">Outlook-Verbindung</h3>
          {status?.configured ? (
            <p className="text-sm text-gray-500 mt-1">
              Status: <span className={`font-medium ${status.status === 'connected' ? 'text-green-700' : 'text-amber-700'}`}>{status.status}</span>
              {status.accountUsername && ` — ${status.accountUsername}`}
              {status.bookingCalendarName && ` — Kalender: ${status.bookingCalendarName}`}
            </p>
          ) : (
            <p className="text-sm text-gray-500 mt-1">Outlook-Integration ist auf diesem Deployment nicht konfiguriert.</p>
          )}
          {status?.lastSyncError && <p className="text-sm text-red-600 mt-1">Letzter Fehler: {status.lastSyncError}</p>}
        </div>
        {status?.configured && (
          <div className="flex gap-2">
            {status.status === 'connected' || status.status === 'reauth-required' ? (
              <>
                <button onClick={handleResync} disabled={busy} className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50">Neu synchronisieren</button>
                <button onClick={handleDisconnect} disabled={busy} className="px-3 py-1.5 text-sm rounded-lg border border-red-300 text-red-700 hover:bg-red-50 disabled:opacity-50">Trennen</button>
                {status.status === 'reauth-required' && (
                  <button onClick={handleConnect} disabled={busy} className="px-3 py-1.5 text-sm rounded-lg bg-primary-500 text-white hover:bg-primary-600 disabled:opacity-50">Erneut verbinden</button>
                )}
              </>
            ) : (
              <button onClick={handleConnect} disabled={busy} className="px-3 py-1.5 text-sm rounded-lg bg-primary-500 text-white hover:bg-primary-600 disabled:opacity-50">Outlook verbinden</button>
            )}
          </div>
        )}
      </div>
      {error && <p className="text-sm text-red-600 mt-2">{error}</p>}
    </div>
  );
};

const BlockDialog: React.FC<{ onClose: () => void; onCreated: () => void }> = ({ onClose, onCreated }) => {
  const today = toDateInputValue(new Date());
  const [form, setForm] = useState<BlockRequest>({ date: today, startTime: '09:00', endTime: '10:00', reason: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      await adminBlockBooking(form);
      onCreated();
      onClose();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50" onClick={onClose}>
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={handleSubmit}
        className="bg-white rounded-lg shadow-lg p-6 w-full max-w-sm space-y-3"
      >
        <h3 className="font-semibold text-gray-800 mb-2">Termin blockieren</h3>
        {error && <div className="error">{error}</div>}
        <label className="block text-sm text-gray-600">
          Datum
          <input type="date" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="block mt-1 w-full border border-gray-300 rounded px-2 py-1" />
        </label>
        <div className="flex gap-2">
          <label className="block text-sm text-gray-600 flex-1">
            Von
            <input type="time" required value={form.startTime} onChange={(e) => setForm({ ...form, startTime: e.target.value })} className="block mt-1 w-full border border-gray-300 rounded px-2 py-1" />
          </label>
          <label className="block text-sm text-gray-600 flex-1">
            Bis
            <input type="time" required value={form.endTime} onChange={(e) => setForm({ ...form, endTime: e.target.value })} className="block mt-1 w-full border border-gray-300 rounded px-2 py-1" />
          </label>
        </div>
        <label className="block text-sm text-gray-600">
          Grund (optional)
          <input value={form.reason ?? ''} onChange={(e) => setForm({ ...form, reason: e.target.value })} className="block mt-1 w-full border border-gray-300 rounded px-2 py-1" />
        </label>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 hover:bg-gray-50">Abbrechen</button>
          <button type="submit" disabled={saving} className="px-3 py-1.5 text-sm rounded-lg bg-primary-500 text-white hover:bg-primary-600 disabled:opacity-50">
            {saving ? 'Speichern…' : 'Blockieren'}
          </button>
        </div>
      </form>
    </div>
  );
};

const AdminBookings: React.FC = () => {
  const today = new Date();
  const [from, setFrom] = useState(toDateInputValue(today));
  const [to, setTo] = useState(toDateInputValue(new Date(today.getTime() + 60 * 24 * 60 * 60 * 1000)));
  const [status, setStatus] = useState('');
  const [bookings, setBookings] = useState<AdminBooking[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [updating, setUpdating] = useState<string | null>(null);
  const [showBlockDialog, setShowBlockDialog] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      // "to" is inclusive from the admin's point of view (the whole selected day), but the backend
      // range is a half-open [from, to) on startUtc — new Date(to) alone is UTC midnight of that
      // day, which excludes almost the entire day. Push the upper bound to the start of the next day.
      const toExclusive = new Date(to);
      toExclusive.setDate(toExclusive.getDate() + 1);
      const data = await adminGetBookings(new Date(from).toISOString(), toExclusive.toISOString(), status || undefined);
      setBookings(data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [from, to, status]);

  useEffect(() => { load(); }, [load]);

  const handleStatusChange = async (booking: AdminBooking, newStatus: 'confirmed' | 'declined' | 'cancelled') => {
    setUpdating(booking.id);
    try {
      const updated = await adminUpdateBookingStatus(booking, newStatus);
      setBookings((prev) => prev.map((b) => (b.id === booking.id ? updated : b)));
    } catch (err: any) {
      setError(err.message);
    } finally {
      setUpdating(null);
    }
  };

  const handleDelete = async (booking: AdminBooking) => {
    if (!window.confirm('Diesen Termin endgültig löschen?')) return;
    try {
      await adminDeleteBooking(booking);
      setBookings((prev) => prev.filter((b) => b.id !== booking.id));
    } catch (err: any) {
      setError(err.message);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Termine</h1>
        <button
          onClick={() => setShowBlockDialog(true)}
          className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 hover:bg-gray-50"
        >
          + Zeit blockieren
        </button>
      </div>

      {showBlockDialog && <BlockDialog onClose={() => setShowBlockDialog(false)} onCreated={load} />}

      <OutlookConnectionCard />

      <div className="bg-white rounded-lg shadow p-4 mb-4 flex flex-wrap gap-3 items-end">
        <label className="text-sm text-gray-600">
          Von
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="block mt-1 border border-gray-300 rounded px-2 py-1" />
        </label>
        <label className="text-sm text-gray-600">
          Bis
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="block mt-1 border border-gray-300 rounded px-2 py-1" />
        </label>
        <label className="text-sm text-gray-600">
          Status
          <select value={status} onChange={(e) => setStatus(e.target.value)} className="block mt-1 border border-gray-300 rounded px-2 py-1">
            <option value="">Alle</option>
            <option value="pending">Zu bestätigen</option>
            <option value="confirmed">Bestätigt</option>
            <option value="cancelled">Storniert</option>
            <option value="declined">Abgelehnt</option>
            <option value="expired">Abgelaufen</option>
          </select>
        </label>
      </div>

      {error && <div className="error mb-4">{error}</div>}

      {loading ? (
        <p className="text-gray-400">Lade Termine…</p>
      ) : bookings.length === 0 ? (
        <p className="text-gray-400">Keine Termine im gewählten Zeitraum.</p>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-left text-gray-500">
              <tr>
                <th className="px-4 py-2">Datum</th>
                <th className="px-4 py-2">Klient</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2">Sync</th>
                <th className="px-4 py-2 text-right">Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {bookings.map((b) => (
                <tr key={b.id} className="border-t border-gray-100">
                  <td className="px-4 py-2 whitespace-nowrap">{formatDateTime(b.startUtc)}</td>
                  <td className="px-4 py-2">
                    <div className="font-medium text-gray-800">{b.name || '—'}</div>
                    <div className="text-gray-500">{b.email}</div>
                  </td>
                  <td className="px-4 py-2">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_BADGE[b.status] ?? 'bg-gray-100 text-gray-600'}`}>{b.status}</span>
                    {b.conflict && <span className="ml-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">Konflikt</span>}
                  </td>
                  <td className="px-4 py-2 text-xs text-gray-500">
                    {b.outlook?.syncStatus && b.outlook.syncStatus !== 'synced' && b.outlook.syncStatus !== 'none' && (
                      <div className={b.outlook.syncStatus === 'failed' ? 'text-red-600' : ''}>Outlook: {b.outlook.syncStatus}</div>
                    )}
                    {/* Always shown (not just on failure) so a successful CRM link is visible and clickable. */}
                    {b.crm?.contactSubmissionId ? (
                      <Link to="/admin/inquiries" className="text-primary-600 hover:underline">
                        Anfrage verknüpft
                      </Link>
                    ) : b.crm?.syncStatus ? (
                      <div className={b.crm.syncStatus === 'failed' ? 'text-red-600' : ''}>CRM: {b.crm.syncStatus}</div>
                    ) : null}
                  </td>
                  <td className="px-4 py-2 text-right whitespace-nowrap">
                    {b.status === 'pending' && (
                      <>
                        <button
                          disabled={updating === b.id}
                          onClick={() => handleStatusChange(b, 'confirmed')}
                          className="text-green-700 hover:underline mr-3 disabled:opacity-50"
                        >
                          Bestätigen
                        </button>
                        <button
                          disabled={updating === b.id}
                          onClick={() => handleStatusChange(b, 'declined')}
                          className="text-red-600 hover:underline mr-3 disabled:opacity-50"
                        >
                          Ablehnen
                        </button>
                      </>
                    )}
                    {b.status === 'confirmed' && (
                      <button
                        disabled={updating === b.id}
                        onClick={() => handleStatusChange(b, 'cancelled')}
                        className="text-red-600 hover:underline mr-3 disabled:opacity-50"
                      >
                        Stornieren
                      </button>
                    )}
                    <button onClick={() => handleDelete(b)} className="text-gray-400 hover:text-gray-600">Löschen</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminBookings;
